import { Link } from 'react-router-dom';

export default function ContentSections() {
  return (
    <>
      {/* What Is Vigorbuy Spreadsheet */}
      <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs md:text-sm font-label font-medium mb-4">
              Overview
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              What Is Vigorbuy Spreadsheet?
            </h2>
          </div>

          <div className="space-y-5 text-foreground-700 leading-relaxed">
            <p className="text-sm md:text-base">
              <strong className="text-foreground-950">Vigorbuy Spreadsheet</strong> is a structured product comparison and data organization system that allows users to view, analyze, and compare product listings in a clean spreadsheet format. Rather than jumping between scattered product pages, users can see everything side by side in one organized interface.
            </p>
            <p className="text-sm md:text-base">
              The vigorbuy spreadsheet approach transforms how people shop online. Instead of opening dozens of browser tabs and manually comparing prices, features, and reviews, the system presents all relevant information in a single, easy-to-scan spreadsheet view. This means less time searching and more time finding the right product.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              {[
                { icon: 'ri-scales-3-line', title: 'Compare products in real time', desc: 'Side-by-side analysis of features, prices, and ratings in one view.' },
                { icon: 'ri-line-chart-line', title: 'Analyze pricing patterns', desc: 'Track price changes and identify the best-value options instantly.' },
                { icon: 'ri-star-line', title: 'Identify best-value options', desc: 'Smart sorting helps you spot the best deals without endless scrolling.' },
                { icon: 'ri-time-line', title: 'Reduce decision-making time', desc: 'Cut hours of research down to minutes with structured comparisons.' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-sm md:text-base font-label font-semibold text-foreground-900 mb-1">
                      {item.title}
                    </h4>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Vigorbuy Spreadsheet */}
      <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs md:text-sm font-label font-medium mb-4">
              Benefits
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              Why Users Choose Vigorbuy Spreadsheet
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              The vigorbuy spreadsheet experience is designed for efficiency and clarity — here is what sets it apart from traditional browsing.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-background-200/70">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-background-200/60">
                  <th className="px-5 py-3.5 text-sm font-label font-semibold text-foreground-900">Feature</th>
                  <th className="px-5 py-3.5 text-sm font-label font-semibold text-primary-600">Vigorbuy Spreadsheet</th>
                  <th className="px-5 py-3.5 text-sm font-label font-semibold text-foreground-600">Traditional Browsing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                {[
                  { feature: 'Product Comparison', v: 'Instant side-by-side', t: 'Manual across tabs' },
                  { feature: 'Data Structure', v: 'Organized spreadsheet', t: 'Scattered pages' },
                  { feature: 'Decision Speed', v: 'Fast and efficient', t: 'Slow and tedious' },
                  { feature: 'Price Visibility', v: 'Clear at a glance', t: 'Hard to track' },
                  { feature: 'User Effort', v: 'Low — one view', t: 'High — many tabs' },
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-background-100 transition-colors">
                    <td className="px-5 py-3 text-sm font-label text-foreground-900">{row.feature}</td>
                    <td className="px-5 py-3 text-sm text-primary-600 font-medium">{row.v}</td>
                    <td className="px-5 py-3 text-sm text-foreground-600">{row.t}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs md:text-sm font-label font-medium mb-4">
              Trust & Safety
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              Safety and Transparency
            </h2>
          </div>

          <div className="bg-background-100 rounded-2xl border border-background-200/70 p-6 md:p-8">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
              <strong className="text-foreground-950">Vigorbuy Spreadsheet</strong> is an informational system designed to help users compare data efficiently. We believe in transparency and encourage users to make informed decisions.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-shield-check-line', text: 'Verify external product sources before purchasing' },
                { icon: 'ri-lock-line', text: 'Avoid sharing sensitive personal data on external sites' },
                { icon: 'ri-bank-card-line', text: 'Use official platforms for all transactions' },
                { icon: 'ri-price-tag-3-line', text: 'Double-check pricing and availability before buying' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${item.icon} text-base`}></i>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed pt-1">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-background-200/70">
              <Link
                to="/vigorbuy-spreadsheet-guide"
                className="inline-flex items-center gap-2 text-sm font-label font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Learn more in our complete guide
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-8 md:py-10 bg-background-100 border-y border-background-200/70">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: 'ri-refresh-line', text: 'Updated Daily Data Structure' },
              { icon: 'ri-table-line', text: 'Spreadsheet-Based Analysis' },
              { icon: 'ri-user-smile-line', text: 'Beginner Friendly Interface' },
              { icon: 'ri-shield-star-line', text: 'Safety-Oriented System' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50 text-primary-500">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <span className="text-xs md:text-sm font-label font-medium text-foreground-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-16 md:py-24 px-4 md:px-6 lg:px-8 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20bright%20modern%20home%20office%20setup%20with%20a%20macbook%20displaying%20colorful%20product%20comparison%20charts%20on%20screen%20surrounded%20by%20fresh%20flowers%20in%20a%20vase%20a%20ceramic%20coffee%20mug%20and%20neatly%20arranged%20fashion%20magazines%20on%20a%20light%20oak%20desk%20morning%20sunlight%20streaming%20in%20creating%20a%20warm%20inviting%20atmosphere%20editorial%20interior%20photography%20with%20a%20clean%20organized%20and%20aspirational%20aesthetic&width=1920&height=800&seq=cta-v2-unique-77&orientation=landscape"
            alt="Start using vigorbuy spreadsheet today"
            title="Vigorbuy Spreadsheet - Get Started"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-4">
            Start Using Vigorbuy Spreadsheet Today
          </h2>
          <p className="text-sm md:text-base text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
            Access structured product insights and make faster decisions with organized data comparison. Join thousands of smart shoppers who have already simplified their product research.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-accent-500 text-background-50 text-sm md:text-base font-label font-semibold hover:bg-accent-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Get Started Now
            </a>
            <Link
              to="/vigorbuy-spreadsheet-guide"
              className="w-full sm:w-auto px-8 py-3.5 rounded-full border-2 border-white/50 text-white text-sm md:text-base font-label font-medium hover:bg-white/10 hover:border-white/70 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Browse the Guide
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}